import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormService } from '../../core/services/form.service';
import { PaymentService } from '../../core/auth/payment.service';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-stripe-card',
    templateUrl: './stripe-card.component.html',
    styleUrls: ['./stripe-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgClass, ReactiveFormsModule]
})
export class StripeCardComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('cardInfo', { static: true }) cardInfo: ElementRef;
  @ViewChild('nameOnCard', { static: true }) nameOnCard: ElementRef;
  @Output() tokenReady: EventEmitter<any> = new EventEmitter<any>();
  card: any;
  cardHandler = this.onChange.bind(this);
  cardError: string;
  loading: boolean;
  nameOnCardValue: string;
  customPaymentDataForm: UntypedFormGroup;
  isAgreedToUserAgreement = true;

  constructor(
    private paymentService: PaymentService,
    private cd: ChangeDetectorRef,
    public formService: FormService
  ) {
    this.customPaymentDataForm = this.formService.createForm(this.formOptions);
  }

  ngOnInit() {
    this.paymentService.userAgreemnentStatus$.subscribe(status => {
      if (status !== null) {
        this.isAgreedToUserAgreement = status;
      }
    });
  }

  ngOnDestroy() {
    if (this.card) {
      // We remove event listener here to keep memory clean
      this.card.removeEventListener('change', this.cardHandler);
      this.card.destroy();
    }
  }

  async ngAfterViewInit() {
    await this.paymentService.initStripe();
    this.initiateCardElement();
  }

  initiateCardElement() {
    // Giving a base style here, but most of the style is in scss file
    const cardStyle = {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    };
    this.card = this.paymentService.elements.create(
      'card', {
        hidePostalCode: false,
        style: cardStyle,
      });
    this.card.mount(this.cardInfo.nativeElement);
    this.card.addEventListener('change', this.cardHandler);
  }

  onChange({error}) {
    if (error) {
      this.cardError = error.message;
    } else {
      this.cardError = null;
    }
    this.cd.detectChanges();
  }

  async createStripeToken() {
    if (this.customPaymentDataForm.controls.nameOnCard.errors) {
      const errObject: ValidationErrors = this.customPaymentDataForm.controls.nameOnCard.errors;
      let errorMessage: string;
      switch (Object.keys(errObject)[0]) {
        case 'required':
          errorMessage = 'You need to enter name on card.';
          break;
        case 'minlength':
          errorMessage = 'The name is too short.';
          break;
        case 'maxlength':
          errorMessage = 'The name is too long.';
          break;
        case 'pattern':
          errorMessage = 'Use Latin letters without spaces at both ends for a full name.';
          break;
        default:
          errorMessage = '"Name on card" field has invalid value.';
          break;
      }
      this.onError({
        message: errorMessage
      });
      this.nameOnCard.nativeElement.focus();
      return;
    }
    const {token, error} = await this.paymentService.stripe.createToken(this.card, {name: this.customPaymentDataForm.value.nameOnCard});
    if (token) {
      this.onSuccess(token);
    } else {
      this.onError(error);
    }
  }
  onSuccess(token) {
    this.loading = true;
    this.tokenReady.emit({token, nameOnCard: this.customPaymentDataForm.value.nameOnCard});
  }

  onError(error) {
    if (error.message) {
      this.cardError = error.message;
    }
    this.loading = false;
  }

  private  get formOptions(): Record<string, unknown> {
    return {
      nameOnCard: [
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          Validators.pattern('^((?:[A-Za-z]+ ?){1,3})(?<! )$')
        ])
      ],
    };
  }
}
